/**
 * The effective measurement system (decision S4): the user's override, else
 * the organization's setting, else metric. MeasurementPreferencesSync (mounted
 * once in App) loads it from GET /users/me/preferences and puts it here; every
 * screen reads it with useMeasurementSystem() or useUnits().
 *
 * A tiny external store (useSyncExternalStore) rather than React context, so
 * it also works inside react-three-fiber canvases and in components rendered
 * without providers. The last value is kept in localStorage per user so a
 * reload does not flash the other unit system.
 */
import { useMemo, useSyncExternalStore } from 'react';
import {
  asMeasurementSystem,
  displayLengthToMeters,
  displaySpeedToMps,
  formatLength,
  formatLongDistance,
  formatScaleLength,
  formatSpeed,
  formatWeight,
  lengthUnit,
  longDistanceUnit,
  metersToDisplayLength,
  metersToLongDistance,
  mpsToDisplaySpeed,
  speedUnit,
  weightUnit,
  type MeasurementSystem,
} from '@/utils/units';

const STORAGE_PREFIX = 'measurement_system:';

let current: MeasurementSystem = 'metric';
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** The effective measurement system right now (outside React). */
export function getMeasurementSystem(): MeasurementSystem {
  return current;
}

/**
 * Sets the effective measurement system (called by MeasurementPreferencesSync,
 * and by tests). `userId` also remembers it for the next page load.
 */
export function setMeasurementSystem(system: MeasurementSystem, userId?: string | null): void {
  const next = asMeasurementSystem(system);
  if (userId) {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${userId}`, next);
    } catch {
      // storage unavailable: the in-memory value still applies
    }
  }
  if (next === current) return;
  current = next;
  emit();
}

/** Restores the last known system for `userId` from localStorage (metric when none). */
export function restoreMeasurementSystem(userId: string | null | undefined): void {
  let stored: string | null = null;
  if (userId) {
    try {
      stored = localStorage.getItem(`${STORAGE_PREFIX}${userId}`);
    } catch {
      stored = null;
    }
  }
  setMeasurementSystem(asMeasurementSystem(stored));
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The effective measurement system ('metric' | 'imperial'); re-renders when it changes. */
export function useMeasurementSystem(): MeasurementSystem {
  return useSyncExternalStore(subscribe, getMeasurementSystem, getMeasurementSystem);
}

/**
 * Formatters and converters bound to the effective system. Stored values go
 * in (metres, m/s, kg); display strings and display-unit numbers come out,
 * and `to*` turns a typed display value back into the stored unit.
 */
export function useUnits() {
  const system = useMeasurementSystem();
  return useMemo(() => ({
    system,
    lengthUnit: lengthUnit(system),
    longDistanceUnit: longDistanceUnit(system),
    speedUnit: speedUnit(system),
    weightUnit: weightUnit(system),
    length: (meters: number, precision = 1) => formatLength(meters, system, precision),
    longDistance: (meters: number, precision = 2) => formatLongDistance(meters, system, precision),
    speed: (mps: number, precision = 1) => formatSpeed(mps, system, precision),
    weight: (kg: number, precision = 1) => formatWeight(kg, system, precision),
    scaleLength: (value: number, scaleUnit: string | null | undefined, precision = 1) =>
      formatScaleLength(value, scaleUnit, system, precision),
    lengthValue: (meters: number) => metersToDisplayLength(meters, system),
    longDistanceValue: (meters: number) => metersToLongDistance(meters, system),
    speedValue: (mps: number) => mpsToDisplaySpeed(mps, system),
    toMeters: (value: number) => displayLengthToMeters(value, system),
    toMps: (value: number) => displaySpeedToMps(value, system),
  }), [system]);
}
